import { useMemo, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useWorkflowDashboard } from '../hooks/workflow';
import { WorkflowContext, type WorkflowContextValue } from './WorkflowContextDef';
import { workflowStatusRoleService } from '../services/workflowStatusRoleService';
import type { UserStatusPermissions } from '../types/workflow/statusRole';

/**
 * Props for WorkflowProvider component.
 */
interface WorkflowProviderProps {
  children: ReactNode;
}

/**
 * Provider component that wraps the application (or workflow section) to provide
 * workflow state to all child components via React Context.
 *
 * CRITICAL: The context value is memoized using useMemo to prevent cascading
 * re-renders. Without memoization, every state change in useWorkflowDashboard
 * would create a new context value object, causing ALL context consumers to
 * re-render even if they only use stable function references.
 *
 * Single Responsibility: This component ONLY provides context. It contains no
 * business logic - all logic is encapsulated in useWorkflowDashboard.
 *
 * Usage:
 * - Import WorkflowProvider from this file
 * - Import useWorkflow hook from '../hooks/useWorkflow'
 * - Import WorkflowContextValue type from './WorkflowContextDef' (or via useWorkflow)
 *
 * @example
 * ```tsx
 * // In App.tsx or a layout component
 * import { WorkflowProvider } from './contexts/WorkflowContext';
 * import { useWorkflow } from './hooks/useWorkflow';
 *
 * function WorkflowLayout() {
 *   return (
 *     <WorkflowProvider>
 *       <WorkflowHeader />
 *       <WorkflowFilters />
 *       <WorkflowContent />
 *       <WorkflowFooter />
 *     </WorkflowProvider>
 *   );
 * }
 *
 * function WorkflowContent() {
 *   const { batches, loading } = useWorkflow();
 *   // ...
 * }
 * ```
 */
export function WorkflowProvider({ children }: WorkflowProviderProps) {
  const dashboard = useWorkflowDashboard();

  // Permissions state
  const [userPermissions, setUserPermissions] = useState<UserStatusPermissions | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // Fetch permissions function (stable reference via useCallback)
  const refetchPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    try {
      const permissions = await workflowStatusRoleService.getMyPermissions();
      setUserPermissions(permissions);
    } catch {
      // Don't throw - permissions being null signals error state
      setUserPermissions(null);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  // Fetch permissions on mount
  useEffect(() => {
    const controller = new AbortController();

    const fetchPermissions = async () => {
      setPermissionsLoading(true);
      try {
        const permissions = await workflowStatusRoleService.getMyPermissions(controller.signal);
        setUserPermissions(permissions);
      } catch (error) {
        // Ignore abort/cancel errors (component unmounted or request canceled)
        if (error instanceof Error && (error.name === 'AbortError' || error.name === 'CanceledError')) {
          return;
        }
        setUserPermissions(null);
      } finally {
        if (!controller.signal.aborted) {
          setPermissionsLoading(false);
        }
      }
    };

    fetchPermissions();

    return () => {
      controller.abort();
    };
  }, []);

  // CRITICAL: Memoize context value to prevent cascading re-renders.
  // Without this, every state change creates a new object reference,
  // causing ALL context consumers to re-render unnecessarily.
  const memoizedValue = useMemo<WorkflowContextValue>(
    () => ({
      // Filters (stable references from useCallback in useWorkflowFilters)
      filters: dashboard.filters,
      setFilter: dashboard.setFilter,
      setFilters: dashboard.setFilters,
      resetFilters: dashboard.resetFilters,
      hasActiveFilters: dashboard.hasActiveFilters,
      activeFilterCount: dashboard.activeFilterCount,

      // Data
      batches: dashboard.batches,
      totalCount: dashboard.totalCount,
      totalPages: dashboard.totalPages,
      stats: dashboard.stats,

      // Loading/Error
      loading: dashboard.loading,
      batchesLoading: dashboard.batchesLoading,
      statsLoading: dashboard.statsLoading,
      error: dashboard.error,

      // Selection (stable references from useCallback in useWorkflowSelection)
      selectedIds: dashboard.selectedIds,
      selectedCount: dashboard.selectedCount,
      isSelected: dashboard.isSelected,
      toggleSelection: dashboard.toggleSelection,
      selectAll: dashboard.selectAll,
      clearSelection: dashboard.clearSelection,

      // Actions
      refetch: dashboard.refetch,

      // Permissions
      userPermissions,
      permissionsLoading,
      refetchPermissions,
    }),
    [
      // Only include values that should trigger re-renders
      dashboard.filters,
      dashboard.batches,
      dashboard.totalCount,
      dashboard.totalPages,
      dashboard.stats,
      dashboard.loading,
      dashboard.batchesLoading,
      dashboard.statsLoading,
      dashboard.error,
      dashboard.selectedIds,
      dashboard.selectedCount,
      dashboard.hasActiveFilters,
      dashboard.activeFilterCount,
      // Stable function references (included for completeness, but shouldn't change)
      dashboard.setFilter,
      dashboard.setFilters,
      dashboard.resetFilters,
      dashboard.isSelected,
      dashboard.toggleSelection,
      dashboard.selectAll,
      dashboard.clearSelection,
      dashboard.refetch,
      // Permissions
      userPermissions,
      permissionsLoading,
      refetchPermissions,
    ]
  );

  return (
    <WorkflowContext.Provider value={memoizedValue}>
      {children}
    </WorkflowContext.Provider>
  );
}
